import { EditorialInput, EditorialPost, EditorialStatus } from '../../../types/editorial';

export const EDITORIAL_TIME_ZONE = 'America/Sao_Paulo';
export const statusLabels: Record<EditorialStatus, string> = {
  draft: 'Rascunho', pending_review: 'Aguardando aprovação', changes_requested: 'Ajustes solicitados',
  scheduled: 'Aprovado e agendado', published: 'Publicado', archived: 'Programação arquivada',
};

export type EditorialForm = Omit<EditorialInput, 'author_url' | 'featured_image' | 'featured_image_alt' | 'scheduled_at'> & {
  author_url: string; featured_image: string; featured_image_alt: string; scheduled_at: string;
};

export const emptyEditorialForm = (): EditorialForm => ({
  title: '', slug: '', excerpt: '', content: '', meta_description: '', author_name: '', author_bio: '',
  author_url: '', featured_image: '', featured_image_alt: '', scheduled_at: '',
});

export function toBrasiliaInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: EDITORIAL_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const value = (name: string) => parts.find(part => part.type === name)?.value;
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`;
}

export function fromBrasiliaInput(value: string): string | null {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) throw new Error('Informe uma data e hora válidas.');
  const date = new Date(`${value}:00-03:00`);
  if (Number.isNaN(date.getTime()) || toBrasiliaInput(date.toISOString()) !== value) {
    throw new Error('Informe uma data e hora válidas.');
  }
  return date.toISOString();
}

export const formatBrasilia = (iso: string | null): string => iso
  ? new Intl.DateTimeFormat('pt-BR', {
    timeZone: EDITORIAL_TIME_ZONE, day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso))
  : 'Sem data definida';

export const formFromPost = (post: EditorialPost): EditorialForm => ({
  title: post.title, slug: post.slug, excerpt: post.excerpt || '', content: post.content || '',
  meta_description: post.meta_description || '', author_name: post.author_name || '', author_bio: post.author_bio || '',
  author_url: post.author_url || '', featured_image: post.featured_image || '',
  featured_image_alt: post.featured_image_alt || '', scheduled_at: toBrasiliaInput(post.scheduled_at),
});

export const inputFromEditorialForm = (form: EditorialForm): EditorialInput => ({
  ...form, title: form.title.trim(), slug: form.slug.trim(),
  author_name: form.author_name.trim(), author_url: form.author_url.trim() || null,
  featured_image: form.featured_image.trim() || null, featured_image_alt: form.featured_image_alt.trim(),
  scheduled_at: fromBrasiliaInput(form.scheduled_at),
});

export const editorialDirty = (form: EditorialForm, post: EditorialPost | null): boolean =>
  JSON.stringify(form) !== JSON.stringify(post ? formFromPost(post) : emptyEditorialForm());

export const canApprovePost = (post: EditorialPost | null, canApprove: boolean, dirty: boolean, now = Date.now()): boolean =>
  !!post && canApprove && !dirty && post.status === 'pending_review' && !!post.scheduled_at && new Date(post.scheduled_at).getTime() > now;

export const isOverdue = (post: EditorialPost, now = Date.now()): boolean =>
  !!post.scheduled_at && new Date(post.scheduled_at).getTime() < now &&
  ['draft', 'pending_review', 'changes_requested'].includes(post.status);

export function agendaGroups(posts: EditorialPost[]): { label: string; posts: EditorialPost[] }[] {
  const groups = new Map<string, EditorialPost[]>();
  [...posts].sort((a, b) => (a.scheduled_at || '9999').localeCompare(b.scheduled_at || '9999')).forEach(post => {
    const local = toBrasiliaInput(post.scheduled_at);
    const day = local ? new Date(`${local.slice(0, 10)}T12:00:00Z`) : null;
    if (day) day.setUTCDate(day.getUTCDate() - (day.getUTCDay() + 6) % 7);
    const label = day ? `Semana de ${new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
    }).format(day)}` : 'Sem data definida';
    groups.set(label, [...(groups.get(label) || []), post]);
  });
  return Array.from(groups, ([label, grouped]) => ({ label, posts: grouped }));
}
